import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { 
  MapPin, Instagram, Phone, CheckCircle, ArrowRight,
  Send, Loader2, Camera, Map, MessageCircle,
  Coffee, UtensilsCrossed, Store, Scissors, Dumbbell, Car, Sparkles
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1 }
};

const slideInLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0 }
};

const slideInRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0 }
};

// 3D Tilt Card Component
const TiltCard = ({ children, className = '' }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseXSpring = useSpring(x);
  const mouseYSpring = useSpring(y);

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["8deg", "-8deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-8deg", "8deg"]);

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const xPct = mouseX / width - 0.5;
    const yPct = mouseY / height - 0.5;
    x.set(xPct);
    y.set(yPct);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        rotateY,
        rotateX,
        transformStyle: "preserve-3d",
      }}
      className={className}
    >
      <div style={{ transform: "translateZ(30px)" }}>
        {children}
      </div>
    </motion.div>
  );
};

// Magnetic Button Component
const MagneticButton = ({ children, className = '', ...props }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set((e.clientX - centerX) * 0.2);
    y.set((e.clientY - centerY) * 0.2);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ x, y }}
      transition={{ type: "spring", stiffness: 150, damping: 15 }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Animated Section wrapper
const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Staggered container for cards
const StaggerContainer = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.15 } }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Card with animation
const AnimatedCard = ({ children, className = '' }) => {
  return (
    <motion.div
      variants={fadeInUp}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Translations for all 11 languages
const translations = {
  en: {
    location: "New Jersey",
    heroTitle1: "We Visit Your Business",
    heroTitle2: "Create Real Content",
    heroTitle3: "And Bring You More Customers",
    heroSubtitle: "We come to your location, take real photos and videos of your business, and turn them into Google Maps and Instagram content that helps people find you, call you, and walk in.",
    heroTagline: "Local. Real. Effective.",
    ctaButton: "First Shoot Free – Apply Now",
    ourWorkLink: "See Our Work",
    whatWeDo: "What We Do",
    whatWeDoDesc: "We don't sell templates or generic marketing. We work on-site, using real content, to help local businesses get more customers.",
    whatWeDoFocus: "Our focus is simple: Make your business easier to find, more trustworthy, and more visible online.",
    googleMapsTitle: "Google Maps Optimization",
    googleMapsDesc: "We improve your Google Maps profile using real photos, videos, and updates taken directly at your business.",
    googleMapsHelps: "This helps you:",
    googleMapsBenefit1: "Show up higher in nearby searches",
    googleMapsBenefit2: "Look active and professional",
    googleMapsBenefit3: "Get more calls and direction requests",
    instagramTitle: "Instagram Content & Stories",
    instagramDesc: "We visit your business and create natural, real content using phone photos and videos.",
    instagramBenefit1: "Photos and short videos of your space, food, or service",
    instagramBenefit2: "Regular posts and stories",
    instagramBenefit3: "Content that feels real, not staged or overproduced",
    contactTitle: "Customer Contact Setup",
    contactDesc: "We make it easy for customers to:",
    contactBenefit1: "Call you",
    contactBenefit2: "Message you",
    contactBenefit3: "Get directions",
    contactNote: "Everything is optimized for one-click contact.",
    howItWorks: "How It Works",
    step1Title: "Visit & Review",
    step1Desc: "We come to your business and review your current online presence.",
    step2Title: "Create & Optimize",
    step2Desc: "We take photos and videos, then update your Google Maps and Instagram.",
    step3Title: "Results",
    step3Desc: "You get more calls, messages, and walk-in customers.",
    monthlyService: "Monthly service",
    noContracts: "No long-term contracts",
    whyWorks: "Why This Works",
    reason1Title: "Real Content, Not Overproduced",
    reason1Desc: "Customers trust real photos and videos taken at your business.",
    reason2Title: "Built for Local Search Behavior",
    reason2Desc: "Local customers search, decide, and act quickly. We position your business at the right moment.",
    reason3Title: "Consistent Local Presence",
    reason3Desc: "Regular updates keep your business visible and relevant.",
    reason4Title: "Focused on Real Results",
    reason4Desc: "Calls, messages, and visits — not vanity metrics.",
    newBusinessBadge: "Special Offer for New Businesses",
    newBusinessTitle: "Just Opened Your Business?",
    newBusinessDesc: "We offer special discounted packages for new businesses.",
    newBusinessDiscount: "20% OFF",
    newBusinessNote: "For businesses in their first 6 months",
    newBusinessCta: "Contact us to learn more",
    whoWeWork: "Who We Work With",
    whoWeWorkDesc: "We work with all types of local businesses, including:",
    includes: "Includes:",
    include1: "On-site photo & video visits",
    include2: "Google Maps optimization & updates",
    include3: "Instagram posts & stories",
    include4: "Contact & call setup",
    include5: "Ongoing local visibility improvements",
    firstMonthFree: "First shoot free",
    cancelAnytime: "Cancel anytime",
    getStarted: "Get Started",
    getStartedQuestion: "Want to See If This Works for Your Business?",
    getStartedDesc: "Try it free for one month.\nNo risk. No obligation.",
    getStartedNote: "We don't sell marketing.\nWe help local businesses get found, trusted, and chosen.",
    thankYou: "Thank You!",
    thankYouDesc: "We'll get back to you within 24 hours.",
    formBusinessName: "Business Name",
    formYourName: "Your Name",
    formEmail: "Email",
    formPhone: "Phone / WhatsApp",
    formBusinessType: "Business Type",
    formMessage: "Message (Optional)",
    formSelectType: "Select type...",
    formSubmit: "Apply Now – First Shoot Free",
    formSending: "Sending...",
    orWhatsApp: "Or message us directly on WhatsApp",
    businesses: ["Restaurants", "Cafes & Coffee Shops", "Bakeries & Pastry Shops", "Pizzerias", "Food Trucks", "Barbershops & Hair Salons", "Nail & Beauty Salons", "Gyms & Fitness Studios", "Auto Repair Shops", "Dry Cleaners", "Pet Grooming", "And other local businesses"]
  },
  tr: {
    location: "New Jersey",
    heroTitle1: "İşletmenizi Ziyaret Ediyoruz",
    heroTitle2: "Gerçek İçerik Üretiyoruz",
    heroTitle3: "Ve Size Daha Fazla Müşteri Getiriyoruz",
    heroSubtitle: "Konumunuza geliyoruz, işletmenizin gerçek fotoğraf ve videolarını çekiyoruz ve bunları insanların sizi bulmasına, aramasına ve ziyaret etmesine yardımcı olan Google Haritalar ve Instagram içeriğine dönüştürüyoruz.",
    heroTagline: "Yerel. Gerçek. Etkili.",
    ctaButton: "İlk Çekim Ücretsiz – Hemen Başvur",
    ourWorkLink: "Çalışmalarımızı Gör",
    whatWeDo: "Ne Yapıyoruz",
    whatWeDoDesc: "Şablon veya genel pazarlama satmıyoruz. Yerel işletmelerin daha fazla müşteri kazanmasına yardımcı olmak için yerinde, gerçek içerik kullanarak çalışıyoruz.",
    whatWeDoFocus: "Odağımız basit: İşletmenizi bulmayı kolaylaştırmak, daha güvenilir ve çevrimiçi daha görünür kılmak.",
    googleMapsTitle: "Google Haritalar Optimizasyonu",
    googleMapsDesc: "Google Haritalar profilinizi işletmenizde doğrudan çekilen gerçek fotoğraflar, videolar ve güncellemelerle geliştiriyoruz.",
    googleMapsHelps: "Bu size yardımcı olur:",
    googleMapsBenefit1: "Yakın aramalarda daha üst sıralarda görünme",
    googleMapsBenefit2: "Aktif ve profesyonel görünme",
    googleMapsBenefit3: "Daha fazla arama ve yol tarifi talebi alma",
    instagramTitle: "Instagram İçerik ve Hikayeler",
    instagramDesc: "İşletmenizi ziyaret ediyoruz ve telefon fotoğrafları ve videoları kullanarak doğal, gerçek içerik oluşturuyoruz.",
    instagramBenefit1: "Mekanınızın, yiyeceklerinizin veya hizmetinizin fotoğraf ve kısa videoları",
    instagramBenefit2: "Düzenli paylaşımlar ve hikayeler",
    instagramBenefit3: "Sahnelenmiş veya aşırı üretilmiş değil, gerçek hissettiren içerik",
    contactTitle: "Müşteri İletişim Kurulumu",
    contactDesc: "Müşterilerin kolayca:",
    contactBenefit1: "Sizi aramasını",
    contactBenefit2: "Size mesaj atmasını",
    contactBenefit3: "Yol tarifi almasını sağlıyoruz",
    contactNote: "Her şey tek tıklama ile iletişim için optimize edilmiştir.",
    howItWorks: "Nasıl Çalışır",
    step1Title: "Ziyaret ve İnceleme",
    step1Desc: "İşletmenize geliyoruz ve mevcut çevrimiçi varlığınızı inceliyoruz.",
    step2Title: "Oluştur ve Optimize Et",
    step2Desc: "Fotoğraf ve video çekiyoruz, ardından Google Haritalar ve Instagram'ınızı güncelliyoruz.",
    step3Title: "Sonuçlar",
    step3Desc: "Daha fazla arama, mesaj ve müşteri ziyareti alıyorsunuz.",
    monthlyService: "Aylık hizmet",
    noContracts: "Uzun vadeli sözleşme yok",
    whyWorks: "Neden İşe Yarar",
    reason1Title: "Aşırı Üretilmiş Değil, Gerçek İçerik",
    reason1Desc: "Müşteriler işletmenizde çekilen gerçek fotoğraf ve videolara güvenir.",
    reason2Title: "Yerel Arama Davranışı İçin Tasarlandı",
    reason2Desc: "Yerel müşteriler arar, karar verir ve hızlıca hareket eder. İşletmenizi doğru anda konumlandırıyoruz.",
    reason3Title: "Tutarlı Yerel Varlık",
    reason3Desc: "Düzenli güncellemeler işletmenizi görünür ve alakalı tutar.",
    reason4Title: "Gerçek Sonuçlara Odaklı",
    reason4Desc: "Aramalar, mesajlar ve ziyaretler — gösteriş metrikleri değil.",
    newBusinessBadge: "Yeni İşletmelere Özel Teklif",
    newBusinessTitle: "İşletmenizi Yeni mi Açtınız?",
    newBusinessDesc: "Yeni işletmeler için özel indirimli paketler sunuyoruz.",
    newBusinessDiscount: "%20 İNDİRİM",
    newBusinessNote: "İlk 6 ayındaki işletmeler için",
    newBusinessCta: "Daha fazla bilgi için bize ulaşın",
    whoWeWork: "Kimlerle Çalışıyoruz",
    whoWeWorkDesc: "Her türlü yerel işletme ile çalışıyoruz, dahil:",
    includes: "Dahil:",
    include1: "Yerinde fotoğraf ve video ziyaretleri",
    include2: "Google Haritalar optimizasyonu ve güncellemeleri",
    include3: "Instagram paylaşımları ve hikayeleri",
    include4: "İletişim ve arama kurulumu",
    include5: "Devam eden yerel görünürlük iyileştirmeleri",
    firstMonthFree: "İlk çekim ücretsiz",
    cancelAnytime: "İstediğiniz zaman iptal edin",
    getStarted: "Başlayın",
    getStartedQuestion: "Bunun İşletmeniz İçin İşe Yarayıp Yaramayacağını Görmek İster misiniz?",
    getStartedDesc: "Bir ay ücretsiz deneyin.\nRisk yok. Zorunluluk yok.",
    getStartedNote: "Pazarlama satmıyoruz.\nYerel işletmelerin bulunmasına, güvenilmesine ve seçilmesine yardımcı oluyoruz.",
    thankYou: "Teşekkürler!",
    thankYouDesc: "24 saat içinde size döneceğiz.",
    formBusinessName: "İşletme Adı",
    formYourName: "Adınız",
    formEmail: "E-posta",
    formPhone: "Telefon / WhatsApp",
    formBusinessType: "İşletme Türü",
    formMessage: "Mesaj (İsteğe Bağlı)",
    formSelectType: "Tür seçin...",
    formSubmit: "Hemen Başvur – İlk Çekim Ücretsiz",
    formSending: "Gönderiliyor...",
    orWhatsApp: "Veya doğrudan WhatsApp'tan bize yazın",
    businesses: ["Restoranlar", "Kafeler ve Kahve Dükkanları", "Pastaneler ve Fırınlar", "Pizzacılar", "Yemek Arabaları", "Berberler ve Kuaförler", "Tırnak ve Güzellik Salonları", "Spor Salonları ve Fitness", "Oto Tamir Servisleri", "Kuru Temizlemeciler", "Pet Kuaförleri", "Ve diğer yerel işletmeler"]
  },
  es: {
    location: "New Jersey",
    heroTitle1: "Visitamos Tu Negocio",
    heroTitle2: "Creamos Contenido Real",
    heroTitle3: "Y Te Traemos Más Clientes",
    heroSubtitle: "Vamos a tu ubicación, tomamos fotos y videos reales de tu negocio, y los convertimos en contenido de Google Maps e Instagram que ayuda a la gente a encontrarte, llamarte y visitarte.",
    heroTagline: "Local. Real. Efectivo.",
    ctaButton: "Primera Sesión Gratis – Aplica Ahora",
    whatWeDo: "Qué Hacemos",
    whatWeDoDesc: "No vendemos plantillas ni marketing genérico. Trabajamos en el sitio, usando contenido real, para ayudar a los negocios locales a conseguir más clientes.",
    whatWeDoFocus: "Nuestro enfoque es simple: Hacer que tu negocio sea más fácil de encontrar, más confiable y más visible en línea.",
    googleMapsTitle: "Google Maps Optimización",
    googleMapsDesc: "Mejoramos tu perfil de Google Maps usando fotos reales, videos y actualizaciones tomadas directamente en tu negocio.",
    googleMapsHelps: "Esto te ayuda a:",
    googleMapsBenefit1: "Aparecer más alto en búsquedas cercanas",
    googleMapsBenefit2: "Lucir activo y profesional",
    googleMapsBenefit3: "Recibir más llamadas y solicitudes de direcciones",
    instagramTitle: "Contenido e Historias de Instagram",
    instagramDesc: "Visitamos tu negocio y creamos contenido natural y real usando fotos y videos del teléfono.",
    instagramBenefit1: "Fotos y videos cortos de tu espacio, comida o servicio",
    instagramBenefit2: "Publicaciones e historias regulares",
    instagramBenefit3: "Contenido que se siente real, no montado o sobreproducido",
    contactTitle: "Configuración de Contacto",
    contactDesc: "Facilitamos que los clientes:",
    contactBenefit1: "Te llamen",
    contactBenefit2: "Te envíen mensajes",
    contactBenefit3: "Obtengan direcciones",
    contactNote: "Todo está optimizado para contacto con un clic.",
    howItWorks: "Cómo Funciona",
    step1Title: "Visita y Revisión",
    step1Desc: "Vamos a tu negocio y revisamos tu presencia en línea actual.",
    step2Title: "Crear y Optimizar",
    step2Desc: "Tomamos fotos y videos, luego actualizamos tu Google Maps e Instagram.",
    step3Title: "Resultados",
    step3Desc: "Recibes más llamadas, mensajes y clientes presenciales.",
    monthlyService: "Servicio mensual",
    noContracts: "Sin contratos a largo plazo",
    whyWorks: "Por Qué Funciona",
    reason1Title: "Contenido Real, No Sobreproducido",
    reason1Desc: "Los clientes confían en fotos y videos reales tomados en tu negocio.",
    reason2Title: "Diseñado para Comportamiento de Búsqueda Local",
    reason2Desc: "Los clientes locales buscan, deciden y actúan rápidamente. Posicionamos tu negocio en el momento adecuado.",
    reason3Title: "Presencia Local Consistente",
    reason3Desc: "Las actualizaciones regulares mantienen tu negocio visible y relevante.",
    reason4Title: "Enfocado en Resultados Reales",
    reason4Desc: "Llamadas, mensajes y visitas — no métricas de vanidad.",
    newBusinessBadge: "Oferta Especial para Nuevos Negocios",
    newBusinessTitle: "¿Acabas de Abrir tu Negocio?",
    newBusinessDesc: "Ofrecemos paquetes con descuentos especiales para nuevos negocios.",
    newBusinessDiscount: "20% DESCUENTO",
    newBusinessNote: "Para negocios en sus primeros 6 meses",
    newBusinessCta: "Contáctanos para saber más",
    whoWeWork: "Con Quién Trabajamos",
    whoWeWorkDesc: "Trabajamos con todo tipo de negocios locales, incluyendo:",
    includes: "Incluye:",
    include1: "Visitas para fotos y videos en sitio",
    include2: "Optimización y actualizaciones de Google Maps",
    include3: "Publicaciones e historias de Instagram",
    include4: "Configuración de contacto y llamadas",
    include5: "Mejoras continuas de visibilidad local",
    firstMonthFree: "Primera sesión gratis",
    cancelAnytime: "Cancela cuando quieras",
    getStarted: "Comenzar",
    getStartedQuestion: "¿Quieres Ver Si Esto Funciona Para Tu Negocio?",
    getStartedDesc: "Pruébalo gratis por un mes.\nSin riesgo. Sin obligación.",
    getStartedNote: "No vendemos marketing.\nAyudamos a los negocios locales a ser encontrados, confiables y elegidos.",
    thankYou: "¡Gracias!",
    thankYouDesc: "Te responderemos dentro de 24 horas.",
    formBusinessName: "Nombre del Negocio",
    formYourName: "Tu Nombre",
    formEmail: "Correo",
    formPhone: "Teléfono / WhatsApp",
    formBusinessType: "Tipo de Negocio",
    formMessage: "Mensaje (Opcional)",
    formSelectType: "Selecciona tipo...",
    formSubmit: "Aplica Ahora – Primera Sesión Gratis",
    formSending: "Enviando...",
    orWhatsApp: "O escríbenos directamente por WhatsApp",
    businesses: ["Restaurantes", "Cafeterías", "Panaderías y Pastelerías", "Pizzerías", "Food Trucks", "Barberías y Peluquerías", "Salones de Uñas y Belleza", "Gimnasios y Fitness", "Talleres de Autos", "Tintorerías", "Peluquerías de Mascotas", "Y otros negocios locales"]
  },
  pt: {
    location: "New Jersey",
    heroTitle1: "Visitamos Seu Negócio",
    heroTitle2: "Criamos Conteúdo Real",
    heroTitle3: "E Trazemos Mais Clientes",
    heroSubtitle: "Vamos até sua localização, tiramos fotos e vídeos reais do seu negócio, e transformamos em conteúdo do Google Maps e Instagram que ajuda as pessoas a te encontrar, ligar e visitar.",
    heroTagline: "Local. Real. Efetivo.",
    ctaButton: "Primeira Sessão Grátis – Aplique Agora",
    whatWeDo: "O Que Fazemos",
    whatWeDoDesc: "Não vendemos templates ou marketing genérico. Trabalhamos no local, usando conteúdo real, para ajudar negócios locais a conseguir mais clientes.",
    whatWeDoFocus: "Nosso foco é simples: Tornar seu negócio mais fácil de encontrar, mais confiável e mais visível online.",
    googleMapsTitle: "Otimização do Google Maps",
    googleMapsDesc: "Melhoramos seu perfil do Google Maps usando fotos reais, vídeos e atualizações feitas diretamente no seu negócio.",
    googleMapsHelps: "Isso te ajuda a:",
    googleMapsBenefit1: "Aparecer mais alto em buscas próximas",
    googleMapsBenefit2: "Parecer ativo e profissional",
    googleMapsBenefit3: "Receber mais ligações e pedidos de direções",
    instagramTitle: "Conteúdo e Stories do Instagram",
    instagramDesc: "Visitamos seu negócio e criamos conteúdo natural e real usando fotos e vídeos do celular.",
    instagramBenefit1: "Fotos e vídeos curtos do seu espaço, comida ou serviço",
    instagramBenefit2: "Posts e stories regulares",
    instagramBenefit3: "Conteúdo que parece real, não encenado ou superproduzido",
    contactTitle: "Configuração de Contato",
    contactDesc: "Facilitamos para os clientes:",
    contactBenefit1: "Te ligar",
    contactBenefit2: "Te enviar mensagem",
    contactBenefit3: "Obter direções",
    contactNote: "Tudo é otimizado para contato com um clique.",
    howItWorks: "Como Funciona",
    step1Title: "Visita e Revisão",
    step1Desc: "Vamos ao seu negócio e revisamos sua presença online atual.",
    step2Title: "Criar e Otimizar",
    step2Desc: "Tiramos fotos e vídeos, depois atualizamos seu Google Maps e Instagram.",
    step3Title: "Resultados",
    step3Desc: "Você recebe mais ligações, mensagens e clientes presenciais.",
    monthlyService: "Serviço mensal",
    noContracts: "Sem contratos de longo prazo",
    whyWorks: "Por Que Funciona",
    reason1Title: "Conteúdo Real, Não Superproduzido",
    reason1Desc: "Clientes confiam em fotos e vídeos reais feitos no seu negócio.",
    reason2Title: "Feito para Comportamento de Busca Local",
    reason2Desc: "Clientes locais buscam, decidem e agem rapidamente. Posicionamos seu negócio no momento certo.",
    reason3Title: "Presença Local Consistente",
    reason3Desc: "Atualizações regulares mantêm seu negócio visível e relevante.",
    reason4Title: "Focado em Resultados Reais",
    reason4Desc: "Ligações, mensagens e visitas — não métricas de vaidade.",
    newBusinessBadge: "Oferta Especial para Novos Negócios",
    newBusinessTitle: "Acabou de Abrir seu Negócio?",
    newBusinessDesc: "Oferecemos pacotes com descontos especiais para novos negócios.",
    newBusinessDiscount: "20% DESCONTO",
    newBusinessNote: "Para negócios em seus primeiros 6 meses",
    newBusinessCta: "Contate-nos para saber mais",
    whoWeWork: "Com Quem Trabalhamos",
    whoWeWorkDesc: "Trabalhamos com todos os tipos de negócios locais, incluindo:",
    includes: "Inclui:",
    include1: "Visitas para fotos e vídeos no local",
    include2: "Otimização e atualizações do Google Maps",
    include3: "Posts e stories do Instagram",
    include4: "Configuração de contato e chamadas",
    include5: "Melhorias contínuas de visibilidade local",
    firstMonthFree: "Primeira sessão grátis",
    cancelAnytime: "Cancele quando quiser",
    getStarted: "Começar",
    getStartedQuestion: "Quer Ver Se Isso Funciona Para Seu Negócio?",
    getStartedDesc: "Experimente grátis por um mês.\nSem risco. Sem obrigação.",
    getStartedNote: "Não vendemos marketing.\nAjudamos negócios locais a serem encontrados, confiáveis e escolhidos.",
    thankYou: "Obrigado!",
    thankYouDesc: "Responderemos em até 24 horas.",
    formBusinessName: "Nome do Negócio",
    formYourName: "Seu Nome",
    formEmail: "Email",
    formPhone: "Telefone / WhatsApp",
    formBusinessType: "Tipo de Negócio",
    formMessage: "Mensagem (Opcional)",
    formSelectType: "Selecione tipo...",
    formSubmit: "Aplique Agora – Primeira Sessão Grátis",
    formSending: "Enviando...",
    orWhatsApp: "Ou nos envie uma mensagem diretamente pelo WhatsApp",
    businesses: ["Restaurantes", "Cafés e Cafeterias", "Padarias e Confeitarias", "Pizzarias", "Food Trucks", "Barbearias e Salões", "Salões de Unhas e Beleza", "Academias e Fitness", "Oficinas Mecânicas", "Lavanderias", "Pet Shops", "E outros negócios locais"]
  },
  it: {
    location: "New Jersey",
    heroTitle1: "Visitiamo la Tua Attività",
    heroTitle2: "Creiamo Contenuti Reali",
    heroTitle3: "E Ti Portiamo Più Clienti",
    heroSubtitle: "Veniamo nella tua sede, scattiamo foto e video reali della tua attività, e li trasformiamo in contenuti di Google Maps e Instagram che aiutano le persone a trovarti, chiamarti e visitarti.",
    heroTagline: "Locale. Reale. Efficace.",
    ctaButton: "Prima Sessione Gratis – Richiedi Ora",
    whatWeDo: "Cosa Facciamo",
    whatWeDoDesc: "Non vendiamo template o marketing generico. Lavoriamo sul posto, usando contenuti reali, per aiutare le attività locali a ottenere più clienti.",
    whatWeDoFocus: "Il nostro focus è semplice: Rendere la tua attività più facile da trovare, più affidabile e più visibile online.",
    googleMapsTitle: "Ottimizzazione Google Maps",
    googleMapsDesc: "Miglioriamo il tuo profilo Google Maps usando foto reali, video e aggiornamenti fatti direttamente nella tua attività.",
    googleMapsHelps: "Questo ti aiuta a:",
    googleMapsBenefit1: "Apparire più in alto nelle ricerche vicine",
    googleMapsBenefit2: "Sembrare attivo e professionale",
    googleMapsBenefit3: "Ricevere più chiamate e richieste di indicazioni",
    instagramTitle: "Contenuti e Storie Instagram",
    instagramDesc: "Visitiamo la tua attività e creiamo contenuti naturali e reali usando foto e video dal telefono.",
    instagramBenefit1: "Foto e video brevi del tuo spazio, cibo o servizio",
    instagramBenefit2: "Post e storie regolari",
    instagramBenefit3: "Contenuti che sembrano reali, non messi in scena o sovraprodotti",
    contactTitle: "Configurazione Contatto Clienti",
    contactDesc: "Rendiamo facile per i clienti:",
    contactBenefit1: "Chiamarti",
    contactBenefit2: "Inviarti messaggi",
    contactBenefit3: "Ottenere indicazioni",
    contactNote: "Tutto è ottimizzato per il contatto con un clic.",
    howItWorks: "Come Funziona",
    step1Title: "Visita e Revisione",
    step1Desc: "Veniamo alla tua attività e rivediamo la tua presenza online attuale.",
    step2Title: "Crea e Ottimizza",
    step2Desc: "Scattiamo foto e video, poi aggiorniamo il tuo Google Maps e Instagram.",
    step3Title: "Risultati",
    step3Desc: "Ricevi più chiamate, messaggi e clienti di persona.",
    monthlyService: "Servizio mensile",
    noContracts: "Nessun contratto a lungo termine",
    whyWorks: "Perché Funziona",
    reason1Title: "Contenuti Reali, Non Sovraprodotti",
    reason1Desc: "I clienti si fidano di foto e video reali fatti nella tua attività.",
    reason2Title: "Progettato per il Comportamento di Ricerca Locale",
    reason2Desc: "I clienti locali cercano, decidono e agiscono rapidamente. Posizioniamo la tua attività al momento giusto.",
    reason3Title: "Presenza Locale Costante",
    reason3Desc: "Gli aggiornamenti regolari mantengono la tua attività visibile e rilevante.",
    reason4Title: "Concentrato sui Risultati Reali",
    reason4Desc: "Chiamate, messaggi e visite — non metriche di vanità.",
    newBusinessBadge: "Offerta Speciale per Nuove Attività",
    newBusinessTitle: "Hai Appena Aperto la Tua Attività?",
    newBusinessDesc: "Offriamo pacchetti scontati speciali per nuove attività.",
    newBusinessDiscount: "20% SCONTO",
    newBusinessNote: "Per attività nei primi 6 mesi",
    newBusinessCta: "Contattaci per saperne di più",
    whoWeWork: "Con Chi Lavoriamo",
    whoWeWorkDesc: "Lavoriamo con tutti i tipi di attività locali, inclusi:",
    includes: "Include:",
    include1: "Visite per foto e video sul posto",
    include2: "Ottimizzazione e aggiornamenti Google Maps",
    include3: "Post e storie Instagram",
    include4: "Configurazione contatto e chiamate",
    include5: "Miglioramenti continui della visibilità locale",
    firstMonthFree: "Prima sessione gratis",
    cancelAnytime: "Cancella quando vuoi",
    getStarted: "Inizia",
    getStartedQuestion: "Vuoi Vedere Se Funziona Per la Tua Attività?",
    getStartedDesc: "Provalo gratis per un mese.\nNessun rischio. Nessun obbligo.",
    getStartedNote: "Non vendiamo marketing.\nAiutiamo le attività locali a essere trovate, fidate e scelte.",
    thankYou: "Grazie!",
    thankYouDesc: "Ti risponderemo entro 24 ore.",
    formBusinessName: "Nome Attività",
    formYourName: "Il Tuo Nome",
    formEmail: "Email",
    formPhone: "Telefono / WhatsApp",
    formBusinessType: "Tipo di Attività",
    formMessage: "Messaggio (Opzionale)",
    formSelectType: "Seleziona tipo...",
    formSubmit: "Richiedi Ora – Prima Sessione Gratis",
    formSending: "Invio...",
    orWhatsApp: "Oppure scrivici direttamente su WhatsApp",
    businesses: ["Ristoranti", "Caffè e Bar", "Panetterie e Pasticcerie", "Pizzerie", "Food Truck", "Barbieri e Parrucchieri", "Saloni di Bellezza", "Palestre e Fitness", "Officine Auto", "Lavanderie", "Toelettatura Animali", "E altre attività locali"]
  },
  ar: {
    location: "نيو جيرسي", heroTitle1: "نزور عملك", heroTitle2: "ننشئ محتوى حقيقي", heroTitle3: "ونجلب لك المزيد من العملاء",
    heroSubtitle: "نأتي إلى موقعك، نلتقط صورًا ومقاطع فيديو حقيقية لعملك، ونحولها إلى محتوى خرائط جوجل وإنستغرام.", heroTagline: "محلي. حقيقي. فعال.", ctaButton: "أول جلسة تصوير مجانية – قدم الآن",
    whatWeDo: "ماذا نفعل", whatWeDoDesc: "لا نبيع قوالب أو تسويق عام. نعمل في الموقع لمساعدة الأعمال المحلية.", whatWeDoFocus: "تركيزنا بسيط: جعل عملك أسهل للعثور عليه.",
    googleMapsTitle: "تحسين خرائط جوجل", googleMapsDesc: "نحسن ملفك على خرائط جوجل باستخدام صور وفيديوهات حقيقية.", googleMapsHelps: "هذا يساعدك على:", googleMapsBenefit1: "الظهور أعلى في عمليات البحث القريبة", googleMapsBenefit2: "تبدو نشطًا ومحترفًا", googleMapsBenefit3: "الحصول على المزيد من المكالمات",
    instagramTitle: "محتوى وقصص إنستغرام", instagramDesc: "نزور عملك وننشئ محتوى طبيعي وحقيقي.", instagramBenefit1: "صور وفيديوهات قصيرة", instagramBenefit2: "منشورات وقصص منتظمة", instagramBenefit3: "محتوى يبدو حقيقيًا",
    contactTitle: "إعداد اتصال العملاء", contactDesc: "نسهل على العملاء:", contactBenefit1: "الاتصال بك", contactBenefit2: "إرسال رسالة لك", contactBenefit3: "الحصول على الاتجاهات", contactNote: "كل شيء محسن للاتصال بنقرة واحدة.",
    howItWorks: "كيف يعمل", step1Title: "الزيارة والمراجعة", step1Desc: "نأتي إلى عملك ونراجع وجودك الحالي.", step2Title: "الإنشاء والتحسين", step2Desc: "نلتقط الصور والفيديوهات ونحدث حساباتك.", step3Title: "النتائج", step3Desc: "تحصل على المزيد من المكالمات والرسائل.",
    monthlyService: "خدمة شهرية", noContracts: "بدون عقود طويلة الأجل",
    whyWorks: "لماذا يعمل هذا", reason1Title: "محتوى حقيقي", reason1Desc: "العملاء يثقون في الصور الحقيقية.", reason2Title: "مصمم للبحث المحلي", reason2Desc: "العملاء المحليون يتصرفون بسرعة.", reason3Title: "وجود محلي ثابت", reason3Desc: "التحديثات المنتظمة تحافظ على ظهورك.", reason4Title: "نتائج حقيقية", reason4Desc: "مكالمات ورسائل وزيارات.",
    newBusinessBadge: "عرض خاص للأعمال الجديدة", newBusinessTitle: "هل فتحت عملك للتو؟", newBusinessDesc: "نقدم باقات مخفضة للأعمال الجديدة.", newBusinessDiscount: "خصم 20%", newBusinessNote: "للأعمال في أول 6 أشهر", newBusinessCta: "تواصل معنا لمعرفة المزيد",
    whoWeWork: "مع من نعمل", whoWeWorkDesc: "نعمل مع جميع أنواع الأعمال المحلية:",
    includes: "يشمل:", include1: "زيارات للتصوير في الموقع", include2: "تحسين خرائط جوجل", include3: "منشورات وقصص إنستغرام", include4: "إعداد الاتصال", include5: "تحسينات مستمرة",
    firstMonthFree: "أول جلسة تصوير مجانية", cancelAnytime: "إلغاء في أي وقت",
    getStarted: "ابدأ", getStartedQuestion: "هل تريد معرفة إذا كان هذا يعمل لعملك؟", getStartedDesc: "جربه مجانًا لمدة شهر.", getStartedNote: "نساعد الأعمال المحلية على أن تُوجد.",
    thankYou: "شكرًا لك!", thankYouDesc: "سنرد عليك خلال 24 ساعة.",
    formBusinessName: "اسم العمل", formYourName: "اسمك", formEmail: "البريد الإلكتروني", formPhone: "الهاتف / واتساب", formBusinessType: "نوع العمل", formMessage: "رسالة (اختياري)", formSelectType: "اختر النوع...", formSubmit: "قدم الآن – أول جلسة تصوير مجانية", formSending: "جاري الإرسال...", orWhatsApp: "أو راسلنا على واتساب",
    businesses: ["مطاعم", "مقاهي", "مخابز", "بيتزا", "عربات طعام", "حلاقين", "صالونات تجميل", "صالات رياضية", "ورش سيارات", "مغاسل", "رعاية حيوانات", "وأعمال أخرى"]
  },
  hi: {
    location: "न्यू जर्सी", heroTitle1: "हम आपके व्यवसाय में आते हैं", heroTitle2: "वास्तविक सामग्री बनाते हैं", heroTitle3: "और आपको अधिक ग्राहक लाते हैं",
    heroSubtitle: "हम आपके स्थान पर आते हैं, फ़ोटो और वीडियो लेते हैं और उन्हें Google Maps और Instagram सामग्री में बदलते हैं।", heroTagline: "स्थानीय। वास्तविक। प्रभावी।", ctaButton: "पहली शूटिंग मुफ्त – अभी आवेदन करें",
    whatWeDo: "हम क्या करते हैं", whatWeDoDesc: "हम टेम्पलेट नहीं बेचते। हम स्थानीय व्यवसायों की मदद करते हैं।", whatWeDoFocus: "हमारा फोकस सरल है: आपके व्यवसाय को खोजना आसान बनाना।",
    googleMapsTitle: "Google Maps अनुकूलन", googleMapsDesc: "हम वास्तविक फ़ोटो और वीडियो से आपकी प्रोफ़ाइल सुधारते हैं।", googleMapsHelps: "यह मदद करता है:", googleMapsBenefit1: "नज़दीकी खोजों में ऊपर दिखाई देना", googleMapsBenefit2: "सक्रिय और पेशेवर दिखना", googleMapsBenefit3: "अधिक कॉल प्राप्त करना",
    instagramTitle: "Instagram सामग्री", instagramDesc: "हम आपके व्यवसाय में आकर प्राकृतिक सामग्री बनाते हैं।", instagramBenefit1: "फ़ोटो और छोटे वीडियो", instagramBenefit2: "नियमित पोस्ट और कहानियां", instagramBenefit3: "वास्तविक सामग्री",
    contactTitle: "ग्राहक संपर्क", contactDesc: "हम ग्राहकों के लिए आसान बनाते हैं:", contactBenefit1: "कॉल करना", contactBenefit2: "संदेश भेजना", contactBenefit3: "दिशा-निर्देश प्राप्त करना", contactNote: "एक-क्लिक संपर्क के लिए अनुकूलित।",
    howItWorks: "कैसे काम करता है", step1Title: "विज़िट और समीक्षा", step1Desc: "हम आपके व्यवसाय में आते हैं।", step2Title: "बनाएं और अनुकूलित करें", step2Desc: "हम फ़ोटो और वीडियो लेते हैं।", step3Title: "परिणाम", step3Desc: "आपको अधिक कॉल और ग्राहक मिलते हैं।",
    monthlyService: "मासिक सेवा", noContracts: "कोई दीर्घकालिक अनुबंध नहीं",
    whyWorks: "यह क्यों काम करता है", reason1Title: "वास्तविक सामग्री", reason1Desc: "ग्राहक वास्तविक फ़ोटो पर भरोसा करते हैं।", reason2Title: "स्थानीय खोज के लिए", reason2Desc: "स्थानीय ग्राहक जल्दी कार्य करते हैं।", reason3Title: "सुसंगत उपस्थिति", reason3Desc: "नियमित अपडेट आपको दृश्यमान रखते हैं।", reason4Title: "वास्तविक परिणाम", reason4Desc: "कॉल, संदेश और विज़िट।",
    newBusinessBadge: "नए व्यवसायों के लिए विशेष", newBusinessTitle: "अभी व्यवसाय खोला?", newBusinessDesc: "हम विशेष छूट पैकेज प्रदान करते हैं।", newBusinessDiscount: "20% छूट", newBusinessNote: "पहले 6 महीनों में व्यवसायों के लिए", newBusinessCta: "अधिक जानने के लिए संपर्क करें",
    whoWeWork: "हम किसके साथ काम करते हैं", whoWeWorkDesc: "सभी प्रकार के स्थानीय व्यवसायों के साथ:",
    includes: "शामिल है:", include1: "साइट पर फ़ोटो विज़िट", include2: "Google Maps अपडेट", include3: "Instagram पोस्ट", include4: "संपर्क सेटअप", include5: "निरंतर सुधार",
    firstMonthFree: "पहली शूटिंग मुफ्त", cancelAnytime: "कभी भी रद्द करें",
    getStarted: "शुरू करें", getStartedQuestion: "देखना चाहते हैं कि यह काम करता है?", getStartedDesc: "एक महीने के लिए मुफ्त में आज़माएं।", getStartedNote: "हम स्थानीय व्यवसायों की मदद करते हैं।",
    thankYou: "धन्यवाद!", thankYouDesc: "हम 24 घंटे में संपर्क करेंगे।",
    formBusinessName: "व्यवसाय का नाम", formYourName: "आपका नाम", formEmail: "ईमेल", formPhone: "फ़ोन / WhatsApp", formBusinessType: "व्यवसाय का प्रकार", formMessage: "संदेश (वैकल्पिक)", formSelectType: "प्रकार चुनें...", formSubmit: "अभी आवेदन करें", formSending: "भेजा जा रहा है...", orWhatsApp: "या WhatsApp पर संपर्क करें",
    businesses: ["रेस्तरां", "कैफे", "बेकरी", "पिज़्ज़ेरिया", "फूड ट्रक", "नाई", "ब्यूटी सैलून", "जिम", "ऑटो रिपेयर", "ड्राई क्लीनर", "पेट ग्रूमिंग", "अन्य व्यवसाय"]
  },
  ko: {
    location: "뉴저지", heroTitle1: "귀하의 비즈니스를 방문합니다", heroTitle2: "실제 콘텐츠를 만듭니다", heroTitle3: "더 많은 고객을 데려옵니다",
    heroSubtitle: "귀하의 위치로 방문하여 실제 사진과 동영상을 촬영하고 Google Maps 및 Instagram 콘텐츠로 변환합니다.", heroTagline: "로컬. 리얼. 효과적.", ctaButton: "첫 촬영 무료 – 지금 신청",
    whatWeDo: "우리가 하는 일", whatWeDoDesc: "템플릿이나 일반적인 마케팅을 판매하지 않습니다.", whatWeDoFocus: "우리의 초점은 간단합니다: 비즈니스를 찾기 쉽게 만드는 것.",
    googleMapsTitle: "Google Maps 최적화", googleMapsDesc: "실제 사진과 동영상으로 프로필을 개선합니다.", googleMapsHelps: "도움이 됩니다:", googleMapsBenefit1: "근처 검색에서 더 높이 표시", googleMapsBenefit2: "활동적이고 전문적으로 보이기", googleMapsBenefit3: "더 많은 전화 받기",
    instagramTitle: "Instagram 콘텐츠", instagramDesc: "비즈니스를 방문하여 자연스러운 콘텐츠를 만듭니다.", instagramBenefit1: "사진과 짧은 동영상", instagramBenefit2: "정기적인 게시물과 스토리", instagramBenefit3: "실제처럼 느껴지는 콘텐츠",
    contactTitle: "고객 연락처 설정", contactDesc: "고객이 쉽게:", contactBenefit1: "전화하기", contactBenefit2: "메시지 보내기", contactBenefit3: "길 안내 받기", contactNote: "원클릭 연락을 위해 최적화.",
    howItWorks: "작동 방식", step1Title: "방문 및 검토", step1Desc: "비즈니스를 방문합니다.", step2Title: "생성 및 최적화", step2Desc: "사진과 동영상을 촬영합니다.", step3Title: "결과", step3Desc: "더 많은 전화와 고객을 받습니다.",
    monthlyService: "월간 서비스", noContracts: "장기 계약 없음",
    whyWorks: "왜 효과가 있는가", reason1Title: "실제 콘텐츠", reason1Desc: "고객은 실제 사진을 신뢰합니다.", reason2Title: "로컬 검색을 위해 설계", reason2Desc: "로컬 고객은 빠르게 행동합니다.", reason3Title: "일관된 존재", reason3Desc: "정기적인 업데이트로 눈에 띕니다.", reason4Title: "실제 결과", reason4Desc: "전화, 메시지, 방문.",
    newBusinessBadge: "신규 비즈니스 특별 제안", newBusinessTitle: "방금 오픈하셨나요?", newBusinessDesc: "특별 할인 패키지를 제공합니다.", newBusinessDiscount: "20% 할인", newBusinessNote: "첫 6개월 비즈니스 대상", newBusinessCta: "자세한 내용은 문의하세요",
    whoWeWork: "함께 일하는 분들", whoWeWorkDesc: "모든 유형의 로컬 비즈니스와 함께:",
    includes: "포함:", include1: "현장 사진 방문", include2: "Google Maps 업데이트", include3: "Instagram 게시물", include4: "연락처 설정", include5: "지속적인 개선",
    firstMonthFree: "첫 촬영 무료", cancelAnytime: "언제든지 취소",
    getStarted: "시작하기", getStartedQuestion: "효과가 있는지 확인하고 싶으신가요?", getStartedDesc: "한 달 무료로 사용해 보세요.", getStartedNote: "로컬 비즈니스를 돕습니다.",
    thankYou: "감사합니다!", thankYouDesc: "24시간 이내에 연락드리겠습니다.",
    formBusinessName: "비즈니스 이름", formYourName: "귀하의 이름", formEmail: "이메일", formPhone: "전화 / WhatsApp", formBusinessType: "비즈니스 유형", formMessage: "메시지 (선택사항)", formSelectType: "유형 선택...", formSubmit: "지금 신청", formSending: "전송 중...", orWhatsApp: "또는 WhatsApp으로 메시지",
    businesses: ["레스토랑", "카페", "베이커리", "피자집", "푸드트럭", "이발소", "뷰티살롱", "체육관", "자동차 수리점", "세탁소", "펫 그루밍", "기타 비즈니스"]
  },
  zh: {
    location: "新泽西", heroTitle1: "我们访问您的企业", heroTitle2: "创建真实内容", heroTitle3: "为您带来更多客户",
    heroSubtitle: "我们来到您的位置，拍摄真实照片和视频，并将它们转化为Google地图和Instagram内容。", heroTagline: "本地。真实。有效。", ctaButton: "首次拍摄免费 – 立即申请",
    whatWeDo: "我们做什么", whatWeDoDesc: "我们不卖模板或通用营销。我们帮助本地企业获得更多客户。", whatWeDoFocus: "我们的重点很简单：让您的企业更容易被找到。",
    googleMapsTitle: "Google地图优化", googleMapsDesc: "我们使用真实照片和视频改善您的资料。", googleMapsHelps: "这可以帮助您：", googleMapsBenefit1: "在附近搜索中排名更高", googleMapsBenefit2: "看起来活跃和专业", googleMapsBenefit3: "获得更多电话",
    instagramTitle: "Instagram内容", instagramDesc: "我们访问您的企业，创建自然、真实的内容。", instagramBenefit1: "照片和短视频", instagramBenefit2: "定期帖子和故事", instagramBenefit3: "感觉真实的内容",
    contactTitle: "客户联系设置", contactDesc: "我们让客户更容易：", contactBenefit1: "给您打电话", contactBenefit2: "给您发消息", contactBenefit3: "获取方向", contactNote: "一切都针对一键联系进行了优化。",
    howItWorks: "工作原理", step1Title: "访问和审查", step1Desc: "我们来到您的企业。", step2Title: "创建和优化", step2Desc: "我们拍摄照片和视频。", step3Title: "结果", step3Desc: "您获得更多电话和客户。",
    monthlyService: "月度服务", noContracts: "无长期合同",
    whyWorks: "为什么有效", reason1Title: "真实内容", reason1Desc: "客户信任真实照片。", reason2Title: "为本地搜索设计", reason2Desc: "本地客户快速行动。", reason3Title: "持续的存在", reason3Desc: "定期更新使您保持可见。", reason4Title: "真实结果", reason4Desc: "电话、消息和访问。",
    newBusinessBadge: "新企业特别优惠", newBusinessTitle: "刚开业？", newBusinessDesc: "我们提供特别折扣套餐。", newBusinessDiscount: "20%折扣", newBusinessNote: "适用于前6个月的企业", newBusinessCta: "联系我们了解更多",
    whoWeWork: "我们与谁合作", whoWeWorkDesc: "与所有类型的本地企业合作：",
    includes: "包括：", include1: "现场照片访问", include2: "Google地图更新", include3: "Instagram帖子", include4: "联系设置", include5: "持续改进",
    firstMonthFree: "首次拍摄免费", cancelAnytime: "随时取消",
    getStarted: "开始", getStartedQuestion: "想看看这是否适合您？", getStartedDesc: "免费试用一个月。", getStartedNote: "我们帮助本地企业。",
    thankYou: "谢谢！", thankYouDesc: "我们将在24小时内回复。",
    formBusinessName: "企业名称", formYourName: "您的姓名", formEmail: "电子邮件", formPhone: "电话 / WhatsApp", formBusinessType: "企业类型", formMessage: "留言（可选）", formSelectType: "选择类型...", formSubmit: "立即申请", formSending: "发送中...", orWhatsApp: "或通过WhatsApp联系",
    businesses: ["餐厅", "咖啡馆", "面包店", "披萨店", "餐车", "理发店", "美容沙龙", "健身房", "汽车修理店", "干洗店", "宠物美容", "其他企业"]
  },
  el: {
    location: "Νιου Τζέρσεϊ", heroTitle1: "Επισκεπτόμαστε την Επιχείρησή Σας", heroTitle2: "Δημιουργούμε Πραγματικό Περιεχόμενο", heroTitle3: "Και Σας Φέρνουμε Περισσότερους Πελάτες",
    heroSubtitle: "Ερχόμαστε στην τοποθεσία σας, τραβάμε πραγματικές φωτογραφίες και βίντεο, και τα μετατρέπουμε σε περιεχόμενο Google Maps και Instagram.", heroTagline: "Τοπικά. Πραγματικά. Αποτελεσματικά.", ctaButton: "Πρώτη Φωτογράφιση Δωρεάν – Κάντε Αίτηση",
    whatWeDo: "Τι Κάνουμε", whatWeDoDesc: "Δεν πουλάμε πρότυπα. Βοηθάμε τοπικές επιχειρήσεις.", whatWeDoFocus: "Η εστίασή μας είναι απλή: Να κάνουμε την επιχείρησή σας πιο εύκολη να βρεθεί.",
    googleMapsTitle: "Βελτιστοποίηση Google Maps", googleMapsDesc: "Βελτιώνουμε το προφίλ σας με πραγματικές φωτογραφίες.", googleMapsHelps: "Αυτό σας βοηθά να:", googleMapsBenefit1: "Εμφανίζεστε ψηλότερα στις αναζητήσεις", googleMapsBenefit2: "Φαίνεστε ενεργοί και επαγγελματίες", googleMapsBenefit3: "Λαμβάνετε περισσότερες κλήσεις",
    instagramTitle: "Περιεχόμενο Instagram", instagramDesc: "Επισκεπτόμαστε και δημιουργούμε φυσικό περιεχόμενο.", instagramBenefit1: "Φωτογραφίες και σύντομα βίντεο", instagramBenefit2: "Τακτικές δημοσιεύσεις", instagramBenefit3: "Πραγματικό περιεχόμενο",
    contactTitle: "Ρύθμιση Επικοινωνίας", contactDesc: "Διευκολύνουμε τους πελάτες να:", contactBenefit1: "Σας καλέσουν", contactBenefit2: "Σας στείλουν μήνυμα", contactBenefit3: "Λάβουν οδηγίες", contactNote: "Βελτιστοποιημένα για επικοινωνία με ένα κλικ.",
    howItWorks: "Πώς Λειτουργεί", step1Title: "Επίσκεψη και Ανασκόπηση", step1Desc: "Ερχόμαστε στην επιχείρησή σας.", step2Title: "Δημιουργία και Βελτιστοποίηση", step2Desc: "Τραβάμε φωτογραφίες και βίντεο.", step3Title: "Αποτελέσματα", step3Desc: "Λαμβάνετε περισσότερες κλήσεις και πελάτες.",
    monthlyService: "Μηνιαία υπηρεσία", noContracts: "Χωρίς μακροχρόνια συμβόλαια",
    whyWorks: "Γιατί Λειτουργεί", reason1Title: "Πραγματικό Περιεχόμενο", reason1Desc: "Οι πελάτες εμπιστεύονται τις πραγματικές φωτογραφίες.", reason2Title: "Σχεδιασμένο για Τοπική Αναζήτηση", reason2Desc: "Οι τοπικοί πελάτες δρουν γρήγορα.", reason3Title: "Συνεπής Παρουσία", reason3Desc: "Οι τακτικές ενημερώσεις σας κρατούν ορατούς.", reason4Title: "Πραγματικά Αποτελέσματα", reason4Desc: "Κλήσεις, μηνύματα και επισκέψεις.",
    newBusinessBadge: "Ειδική Προσφορά για Νέες Επιχειρήσεις", newBusinessTitle: "Μόλις Άνοιξες;", newBusinessDesc: "Προσφέρουμε ειδικά εκπτωτικά πακέτα.", newBusinessDiscount: "20% ΕΚΠΤΩΣΗ", newBusinessNote: "Για επιχειρήσεις στους πρώτους 6 μήνες", newBusinessCta: "Επικοινωνήστε μαζί μας",
    whoWeWork: "Με Ποιους Συνεργαζόμαστε", whoWeWorkDesc: "Με όλους τους τύπους τοπικών επιχειρήσεων:",
    includes: "Περιλαμβάνει:", include1: "Επιτόπιες επισκέψεις φωτογραφίας", include2: "Ενημερώσεις Google Maps", include3: "Δημοσιεύσεις Instagram", include4: "Ρύθμιση επικοινωνίας", include5: "Συνεχείς βελτιώσεις",
    firstMonthFree: "Πρώτη φωτογράφιση δωρεάν", cancelAnytime: "Ακύρωση ανά πάσα στιγμή",
    getStarted: "Ξεκινήστε", getStartedQuestion: "Θέλετε να δείτε αν λειτουργεί;", getStartedDesc: "Δοκιμάστε το δωρεάν για έναν μήνα.", getStartedNote: "Βοηθάμε τις τοπικές επιχειρήσεις.",
    thankYou: "Ευχαριστούμε!", thankYouDesc: "Θα επικοινωνήσουμε εντός 24 ωρών.",
    formBusinessName: "Όνομα Επιχείρησης", formYourName: "Το Όνομά Σας", formEmail: "Email", formPhone: "Τηλέφωνο / WhatsApp", formBusinessType: "Τύπος Επιχείρησης", formMessage: "Μήνυμα (Προαιρετικό)", formSelectType: "Επιλέξτε τύπο...", formSubmit: "Υποβολή Τώρα", formSending: "Αποστολή...", orWhatsApp: "Ή στείλτε μήνυμα στο WhatsApp",
    businesses: ["Εστιατόρια", "Καφετέριες", "Αρτοποιεία", "Πιτσαρίες", "Food Trucks", "Κουρεία", "Σαλόνια Ομορφιάς", "Γυμναστήρια", "Συνεργεία Αυτοκινήτων", "Καθαριστήρια", "Pet Grooming", "Άλλες επιχειρήσεις"]
  },
  gu: {
    location: "ન્યુ જર્સી", heroTitle1: "અમે તમારા વ્યવસાયની મુલાકાત લઈએ છીએ", heroTitle2: "વાસ્તવિક સામગ્રી બનાવીએ છીએ", heroTitle3: "અને તમને વધુ ગ્રાહકો લાવીએ છીએ",
    heroSubtitle: "અમે તમારા સ્થાન પર આવીએ છીએ, ફોટા અને વિડિયો લઈએ છીએ અને તેમને Google Maps અને Instagram સામગ્રીમાં રૂપાંતરિત કરીએ છીએ.", heroTagline: "સ્થાનિક. વાસ્તવિક. અસરકારક.", ctaButton: "પ્રથમ શૂટિંગ મફત – હવે અરજી કરો",
    whatWeDo: "અમે શું કરીએ છીએ", whatWeDoDesc: "અમે ટેમ્પલેટ વેચતા નથી. અમે સ્થાનિક વ્યવસાયોને મદદ કરીએ છીએ.", whatWeDoFocus: "અમારું ધ્યાન સરળ છે: તમારા વ્યવસાયને શોધવામાં સરળ બનાવવો.",
    googleMapsTitle: "Google Maps ઑપ્ટિમાઇઝેશન", googleMapsDesc: "અમે વાસ્તવિક ફોટા અને વિડિયોથી તમારી પ્રોફાઇલ સુધારીએ છીએ.", googleMapsHelps: "આ તમને મદદ કરે છે:", googleMapsBenefit1: "નજીકની શોધમાં ઊંચા દેખાવા", googleMapsBenefit2: "સક્રિય અને વ્યાવસાયિક દેખાવા", googleMapsBenefit3: "વધુ કૉલ મેળવવા",
    instagramTitle: "Instagram સામગ્રી", instagramDesc: "અમે તમારા વ્યવસાયની મુલાકાત લઈએ છીએ અને કુદરતી સામગ્રી બનાવીએ છીએ.", instagramBenefit1: "ફોટા અને ટૂંકા વિડિયો", instagramBenefit2: "નિયમિત પોસ્ટ્સ", instagramBenefit3: "વાસ્તવિક સામગ્રી",
    contactTitle: "ગ્રાહક સંપર્ક", contactDesc: "અમે ગ્રાહકો માટે સરળ બનાવીએ છીએ:", contactBenefit1: "કૉલ કરવા", contactBenefit2: "સંદેશ મોકલવા", contactBenefit3: "દિશાઓ મેળવવા", contactNote: "એક-ક્લિક સંપર્ક માટે ઑપ્ટિમાઇઝ્ડ.",
    howItWorks: "કેવી રીતે કામ કરે છે", step1Title: "મુલાકાત અને સમીક્ષા", step1Desc: "અમે તમારા વ્યવસાયમાં આવીએ છીએ.", step2Title: "બનાવો અને ઑપ્ટિમાઇઝ કરો", step2Desc: "અમે ફોટા અને વિડિયો લઈએ છીએ.", step3Title: "પરિણામો", step3Desc: "તમને વધુ કૉલ્સ અને ગ્રાહકો મળે છે.",
    monthlyService: "માસિક સેવા", noContracts: "લાંબા ગાળાના કરાર નથી",
    whyWorks: "આ કેમ કામ કરે છે", reason1Title: "વાસ્તવિક સામગ્રી", reason1Desc: "ગ્રાહકો વાસ્તવિક ફોટા પર વિશ્વાસ કરે છે.", reason2Title: "સ્થાનિક શોધ માટે બનાવેલ", reason2Desc: "સ્થાનિક ગ્રાહકો ઝડપથી કાર્ય કરે છે.", reason3Title: "સુસંગત હાજરી", reason3Desc: "નિયમિત અપડેટ્સ તમને દૃશ્યમાન રાખે છે.", reason4Title: "વાસ્તવિક પરિણામો", reason4Desc: "કૉલ્સ, સંદેશાઓ અને મુલાકાતો.",
    newBusinessBadge: "નવા વ્યવસાયો માટે વિશેષ", newBusinessTitle: "તમારો વ્યવસાય હમણાં જ ખોલ્યો?", newBusinessDesc: "અમે વિશેષ ડિસ્કાઉન્ટ પેકેજ ઓફર કરીએ છીએ.", newBusinessDiscount: "20% છૂટ", newBusinessNote: "પ્રથમ 6 મહિનામાં વ્યવસાયો માટે", newBusinessCta: "વધુ જાણવા માટે અમારો સંપર્ક કરો",
    whoWeWork: "અમે કોની સાથે કામ કરીએ છીએ", whoWeWorkDesc: "તમામ પ્રકારના સ્થાનિક વ્યવસાયો સાથે:",
    includes: "સામેલ છે:", include1: "સાઇટ પર ફોટો મુલાકાતો", include2: "Google Maps અપડેટ્સ", include3: "Instagram પોસ્ટ્સ", include4: "સંપર્ક સેટઅપ", include5: "ચાલુ સુધારણાઓ",
    firstMonthFree: "પ્રથમ શૂટિંગ મફત", cancelAnytime: "ગમે ત્યારે રદ કરો",
    getStarted: "શરૂ કરો", getStartedQuestion: "જોવા માંગો છો કે આ કામ કરે છે?", getStartedDesc: "એક મહિના માટે મફત અજમાવો.", getStartedNote: "અમે સ્થાનિક વ્યવસાયોને મદદ કરીએ છીએ.",
    thankYou: "આભાર!", thankYouDesc: "અમે 24 કલાકની અંદર સંપર્ક કરીશું.",
    formBusinessName: "વ્યવસાયનું નામ", formYourName: "તમારું નામ", formEmail: "ઇમેઇલ", formPhone: "ફોન / WhatsApp", formBusinessType: "વ્યવસાયનો પ્રકાર", formMessage: "સંદેશ (વૈકલ્પિક)", formSelectType: "પ્રકાર પસંદ કરો...", formSubmit: "હવે અરજી કરો", formSending: "મોકલી રહ્યા છીએ...", orWhatsApp: "અથવા WhatsApp પર સંપર્ક કરો",
    businesses: ["રેસ્ટોરન્ટ", "કેફે", "બેકરી", "પિઝેરિયા", "ફૂડ ટ્રક", "બાર્બરશોપ", "બ્યુટી સલૂન", "જિમ", "ઓટો રિપેર", "ડ્રાય ક્લીનર", "પેટ ગ્રૂમિંગ", "અન્ય વ્યવસાયો"]
  }
};

const businessIcons = [UtensilsCrossed, Coffee, Store, UtensilsCrossed, Store, Scissors, Sparkles, Dumbbell, Car, Store, Store, MapPin];

const MorristownVisibilityPage = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  
  const [formData, setFormData] = useState({
    businessName: '',
    name: '',
    email: '',
    phone: '',
    businessType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: `Business: ${formData.businessName}\nType: ${formData.businessType}\n\n${formData.message}`,
          source: 'Local Business Page'
        })
      });
      
      if (response.ok) {
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const businessTypes = t.businesses;

  return (
    <div className="min-h-screen bg-transparent text-white relative z-10">
      
      {/* HERO SECTION */}
      <section className="relative min-h-[90vh] flex items-center justify-center px-4 pt-20 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#38BDF8]/5 via-transparent to-transparent" />
        
        {/* Animated background particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-[#38BDF8]/30 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.3, 0.8, 0.3],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8"
          >
            <MapPin size={16} className="text-[#38BDF8]" />
            <span className="text-sm font-medium text-white/70">{t.location}</span>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
          >
            <span className="text-white">{t.heroTitle1}</span>
            <br />
            <motion.span 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              className="text-white"
            >
              {t.heroTitle2}
            </motion.span>
            <br />
            <motion.span 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] bg-clip-text text-transparent inline-block"
            >
              {t.heroTitle3}
            </motion.span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.1 }}
            className="text-base sm:text-lg text-white/60 max-w-2xl mx-auto mb-4 leading-relaxed"
          >
            {t.heroSubtitle}
          </motion.p>

          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 1.3 }}
            className="text-[#38BDF8] font-semibold text-lg mb-8"
          >
            {t.heroTagline}
          </motion.p>

          <MagneticButton>
            <motion.a 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.5 }}
              whileHover={{ scale: 1.08, boxShadow: "0 0 60px rgba(56,189,248,0.6)" }}
              whileTap={{ scale: 0.95 }}
              href="#apply"
              className="group inline-flex items-center gap-3 h-14 px-8 rounded-full bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] text-white font-bold text-lg shadow-[0_0_30px_rgba(56,189,248,0.3)] transition-all duration-300"
            >
              <span>{t.ctaButton}</span>
              <motion.span
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <ArrowRight size={20} />
              </motion.span>
            </motion.a>
          </MagneticButton>

          {/* Our Work Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.7 }}
            className="mt-6"
          >
            <Link 
              to="/our-work"
              className="inline-flex items-center gap-3 h-12 px-8 rounded-full bg-white/10 border border-white/20 text-white font-semibold text-base hover:bg-white/20 hover:border-[#38BDF8]/50 hover:text-[#38BDF8] transition-all duration-300 shadow-lg"
            >
              <span>{t.ourWorkLink || 'See Our Work'}</span>
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* WHAT WE DO SECTION */}
      <section className="py-20 px-4" id="what-we-do">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">{t.whatWeDo}</h2>
            <p className="text-white/60 max-w-2xl mx-auto">{t.whatWeDoDesc}</p>
            <p className="text-white/50 mt-4 text-sm">{t.whatWeDoFocus}</p>
          </AnimatedSection>

          <StaggerContainer className="grid md:grid-cols-3 gap-6">
            {/* Google Maps */}
            <AnimatedCard>
              <TiltCard className="h-full">
                <motion.div 
                  className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 h-full cursor-pointer"
                  whileHover={{ 
                    borderColor: 'rgba(56, 189, 248, 0.5)',
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    y: -8
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div 
                    className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#38BDF8]/20 to-[#38BDF8]/5 flex items-center justify-center mb-4"
                    whileHover={{ scale: 1.2, rotate: 10, backgroundColor: 'rgba(56, 189, 248, 0.3)' }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <Map size={24} className="text-[#38BDF8]" />
                  </motion.div>
                  <h3 className="text-xl font-bold mb-3">{t.googleMapsTitle}</h3>
                  <p className="text-white/60 text-sm mb-4">{t.googleMapsDesc}</p>
                  <p className="text-white/50 text-sm mb-2">{t.googleMapsHelps}</p>
                  <ul className="space-y-2 text-sm text-white/60">
                    <li className="flex items-center gap-2"><CheckCircle size={14} className="text-[#38BDF8]" />{t.googleMapsBenefit1}</li>
                    <li className="flex items-center gap-2"><CheckCircle size={14} className="text-[#38BDF8]" />{t.googleMapsBenefit2}</li>
                    <li className="flex items-center gap-2"><CheckCircle size={14} className="text-[#38BDF8]" />{t.googleMapsBenefit3}</li>
                  </ul>
                </motion.div>
              </TiltCard>
            </AnimatedCard>

            {/* Instagram */}
            <AnimatedCard>
              <TiltCard className="h-full">
                <motion.div 
                  className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 h-full cursor-pointer"
                  whileHover={{ 
                    borderColor: 'rgba(228, 64, 95, 0.5)',
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    y: -8
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div 
                    className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E4405F]/20 to-[#E4405F]/5 flex items-center justify-center mb-4"
                    whileHover={{ scale: 1.2, rotate: -10, backgroundColor: 'rgba(228, 64, 95, 0.3)' }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <Instagram size={24} className="text-[#E4405F]" />
                  </motion.div>
                  <h3 className="text-xl font-bold mb-3">{t.instagramTitle}</h3>
                  <p className="text-white/60 text-sm mb-4">{t.instagramDesc}</p>
                  <ul className="space-y-2 text-sm text-white/60">
                    <li className="flex items-center gap-2"><CheckCircle size={14} className="text-[#E4405F]" />{t.instagramBenefit1}</li>
                    <li className="flex items-center gap-2"><CheckCircle size={14} className="text-[#E4405F]" />{t.instagramBenefit2}</li>
                    <li className="flex items-center gap-2"><CheckCircle size={14} className="text-[#E4405F]" />{t.instagramBenefit3}</li>
                  </ul>
                </motion.div>
              </TiltCard>
            </AnimatedCard>

            {/* Contact Setup */}
            <AnimatedCard>
              <TiltCard className="h-full">
                <motion.div 
                  className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 h-full cursor-pointer"
                  whileHover={{ 
                    borderColor: 'rgba(16, 185, 129, 0.5)',
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    y: -8
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div 
                    className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#10B981]/20 to-[#10B981]/5 flex items-center justify-center mb-4"
                    whileHover={{ scale: 1.2, rotate: 10, backgroundColor: 'rgba(16, 185, 129, 0.3)' }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <Phone size={24} className="text-[#10B981]" />
                  </motion.div>
                  <h3 className="text-xl font-bold mb-3">{t.contactTitle}</h3>
                  <p className="text-white/60 text-sm mb-4">{t.contactDesc}</p>
                  <ul className="space-y-2 text-sm text-white/60">
                    <li className="flex items-center gap-2"><CheckCircle size={14} className="text-[#10B981]" />{t.contactBenefit1}</li>
                    <li className="flex items-center gap-2"><CheckCircle size={14} className="text-[#10B981]" />{t.contactBenefit2}</li>
                    <li className="flex items-center gap-2"><CheckCircle size={14} className="text-[#10B981]" />{t.contactBenefit3}</li>
                  </ul>
                  <p className="text-white/50 text-sm mt-4">{t.contactNote}</p>
                </motion.div>
              </TiltCard>
            </AnimatedCard>
          </StaggerContainer>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section className="py-20 px-4 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
        <div className="max-w-4xl mx-auto">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16">{t.howItWorks}</h2>
          </AnimatedSection>
          <StaggerContainer className="grid md:grid-cols-3 gap-8">
            {[
              { num: '01', title: t.step1Title, desc: t.step1Desc },
              { num: '02', title: t.step2Title, desc: t.step2Desc },
              { num: '03', title: t.step3Title, desc: t.step3Desc }
            ].map((step, i) => (
              <AnimatedCard key={i}>
                <motion.div 
                  className="text-center p-6 rounded-2xl cursor-pointer"
                  whileHover={{ 
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    y: -5
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div 
                    className="text-5xl font-bold text-[#38BDF8]/30 mb-2"
                    whileHover={{ scale: 1.2, color: 'rgba(56, 189, 248, 0.8)' }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    {step.num}
                  </motion.div>
                  <motion.div 
                    className="w-12 h-1 bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] mx-auto mb-4 rounded-full"
                    initial={{ width: 0 }}
                    whileInView={{ width: 48 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.2 }}
                  />
                  <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                  <p className="text-white/60 text-sm">{step.desc}</p>
                </motion.div>
              </AnimatedCard>
            ))}
          </StaggerContainer>
          <AnimatedSection delay={0.4} className="flex flex-wrap justify-center gap-4 mt-12 text-sm text-white/60">
            <motion.span 
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5"
              whileHover={{ scale: 1.05, backgroundColor: 'rgba(56, 189, 248, 0.1)' }}
            >
              <CheckCircle size={16} className="text-[#38BDF8]" />{t.monthlyService}
            </motion.span>
            <motion.span 
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5"
              whileHover={{ scale: 1.05, backgroundColor: 'rgba(56, 189, 248, 0.1)' }}
            >
              <CheckCircle size={16} className="text-[#38BDF8]" />{t.noContracts}
            </motion.span>
          </AnimatedSection>
        </div>
      </section>

      {/* WHY THIS WORKS SECTION */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16">{t.whyWorks}</h2>
          </AnimatedSection>
          <StaggerContainer className="grid sm:grid-cols-2 gap-6">
            {[
              { title: t.reason1Title, desc: t.reason1Desc, icon: Camera },
              { title: t.reason2Title, desc: t.reason2Desc, icon: MapPin },
              { title: t.reason3Title, desc: t.reason3Desc, icon: Instagram },
              { title: t.reason4Title, desc: t.reason4Desc, icon: Phone }
            ].map((reason, i) => (
              <AnimatedCard key={i}>
                <motion.div 
                  className="flex gap-4 p-5 rounded-xl bg-white/[0.02] border border-white/5 cursor-pointer"
                  whileHover={{ 
                    borderColor: 'rgba(56, 189, 248, 0.3)',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    x: 5
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div 
                    className="w-10 h-10 rounded-lg bg-[#38BDF8]/10 flex items-center justify-center flex-shrink-0"
                    whileHover={{ scale: 1.2, rotate: 10, backgroundColor: 'rgba(56, 189, 248, 0.25)' }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <reason.icon size={20} className="text-[#38BDF8]" />
                  </motion.div>
                  <div>
                    <h3 className="font-bold mb-1">{reason.title}</h3>
                    <p className="text-white/60 text-sm">{reason.desc}</p>
                  </div>
                </motion.div>
              </AnimatedCard>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* NEW BUSINESS OFFER */}
      <section className="py-16 px-4">
        <AnimatedSection className="max-w-3xl mx-auto">
          <motion.div 
            className="relative bg-gradient-to-br from-[#38BDF8]/10 to-[#0EA5E9]/5 border border-[#38BDF8]/20 rounded-3xl p-8 sm:p-12 text-center overflow-hidden"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#38BDF8]/10 rounded-full blur-3xl" />
            <motion.div
              className="absolute -bottom-10 -left-10 w-40 h-40 bg-[#0EA5E9]/10 rounded-full blur-3xl"
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.span 
              className="inline-block px-3 py-1 rounded-full bg-[#38BDF8]/20 text-[#38BDF8] text-xs font-semibold mb-4"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              {t.newBusinessBadge}
            </motion.span>
            <h3 className="text-2xl sm:text-3xl font-bold mb-2">{t.newBusinessTitle}</h3>
            <p className="text-white/60 mb-6">{t.newBusinessDesc}</p>
            <motion.div 
              className="inline-block px-6 py-3 rounded-xl bg-[#38BDF8] text-black font-bold text-2xl mb-4"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              {t.newBusinessDiscount}
            </motion.div>
            <p className="text-white/50 text-sm mb-6">{t.newBusinessNote}</p>
            <motion.a 
              href="#apply" 
              className="inline-flex items-center gap-2 text-[#38BDF8] font-semibold hover:underline"
              whileHover={{ x: 5 }}
            >
              {t.newBusinessCta}<ArrowRight size={16} />
            </motion.a>
          </motion.div>
        </AnimatedSection>
      </section>

      {/* WHO WE WORK WITH */}
      <section className="py-20 px-4 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">{t.whoWeWork}</h2>
            <p className="text-white/60">{t.whoWeWorkDesc}</p>
          </AnimatedSection>
          <StaggerContainer className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {t.businesses.map((biz, i) => {
              const Icon = businessIcons[i] || MapPin;
              return (
                <AnimatedCard key={i}>
                  <motion.div 
                    className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/5 text-sm text-white/70 cursor-pointer"
                    whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(56, 189, 248, 0.3)' }}
                  >
                    <Icon size={16} className="text-[#38BDF8]" />
                    <span>{biz}</span>
                  </motion.div>
                </AnimatedCard>
              );
            })}
          </StaggerContainer>

          <AnimatedSection delay={0.3} className="mt-16 p-6 rounded-2xl bg-white/[0.02] border border-white/10">
            <h3 className="text-xl font-bold mb-4 text-center">{t.includes}</h3>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              {[t.include1, t.include2, t.include3, t.include4, t.include5].map((item, i) => (
                <motion.div 
                  key={i} 
                  className="flex items-center gap-2 text-white/70"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <CheckCircle size={16} className="text-[#38BDF8]" />{item}
                </motion.div>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-6 pt-6 border-t border-white/10 text-sm">
              <span className="flex items-center gap-2 text-[#38BDF8]"><CheckCircle size={16} />{t.firstMonthFree}</span>
              <span className="flex items-center gap-2 text-[#38BDF8]"><CheckCircle size={16} />{t.noContracts}</span>
              <span className="flex items-center gap-2 text-[#38BDF8]"><CheckCircle size={16} />{t.cancelAnytime}</span>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* GET STARTED / FORM SECTION */}
      <section className="py-20 px-4" id="apply">
        <div className="max-w-xl mx-auto">
          <AnimatedSection className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">{t.getStarted}</h2>
            <p className="text-xl text-white/80 mb-2">{t.getStartedQuestion}</p>
            <p className="text-white/60 whitespace-pre-line">{t.getStartedDesc}</p>
            <p className="text-white/50 text-sm mt-4 whitespace-pre-line">{t.getStartedNote}</p>
          </AnimatedSection>

          {isSubmitted ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center py-12 px-6 rounded-2xl bg-gradient-to-br from-[#38BDF8]/10 to-transparent border border-[#38BDF8]/20"
            >
              <motion.div 
                className="w-16 h-16 rounded-full bg-[#38BDF8]/20 flex items-center justify-center mx-auto mb-4"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
              >
                <CheckCircle size={32} className="text-[#38BDF8]" />
              </motion.div>
              <h3 className="text-2xl font-bold mb-2">{t.thankYou}</h3>
              <p className="text-white/60">{t.thankYouDesc}</p>
            </motion.div>
          ) : (
            <AnimatedSection>
              <motion.form 
                onSubmit={handleSubmit} 
                className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 sm:p-8"
                whileHover={{ borderColor: 'rgba(56, 189, 248, 0.2)' }}
              >
                <div className="grid gap-4">
                  <div>
                    <label className="block text-sm text-white/60 mb-2">{t.formBusinessName}</label>
                    <input type="text" required value={formData.businessName} onChange={(e) => setFormData({...formData, businessName: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#38BDF8] focus:outline-none focus:ring-2 focus:ring-[#38BDF8]/20 transition-all duration-300" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-white/60 mb-2">{t.formYourName}</label>
                      <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#38BDF8] focus:outline-none focus:ring-2 focus:ring-[#38BDF8]/20 transition-all duration-300" />
                    </div>
                    <div>
                      <label className="block text-sm text-white/60 mb-2">{t.formEmail}</label>
                      <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#38BDF8] focus:outline-none focus:ring-2 focus:ring-[#38BDF8]/20 transition-all duration-300" />
                    </div>
                  </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-white/60 mb-2">{t.formPhone}</label>
                    <input type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#38BDF8] focus:outline-none focus:ring-2 focus:ring-[#38BDF8]/20 transition-all duration-300" />
                  </div>
                  <div>
                    <label className="block text-sm text-white/60 mb-2">{t.formBusinessType}</label>
                    <select required value={formData.businessType} onChange={(e) => setFormData({...formData, businessType: e.target.value})} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#38BDF8] focus:outline-none focus:ring-2 focus:ring-[#38BDF8]/20 transition-all duration-300">
                      <option value="" className="bg-[#0a0a0a]">{t.formSelectType}</option>
                      {businessTypes.map((type, i) => (<option key={i} value={type} className="bg-[#0a0a0a]">{type}</option>))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-2">{t.formMessage}</label>
                  <textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} rows={3} className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#38BDF8] focus:outline-none focus:ring-2 focus:ring-[#38BDF8]/20 transition-all duration-300 resize-none" />
                </div>
                <motion.button 
                  type="submit" 
                  disabled={isSubmitting} 
                  className="w-full h-14 rounded-xl bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] text-white font-bold text-lg flex items-center justify-center gap-2 hover:shadow-[0_0_30px_rgba(56,189,248,0.4)] transition-all disabled:opacity-50"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isSubmitting ? (<><Loader2 size={20} className="animate-spin" />{t.formSending}</>) : (<><Send size={20} />{t.formSubmit}</>)}
                </motion.button>
              </div>
              <div className="mt-6 pt-6 border-t border-white/10 text-center">
                <p className="text-white/50 text-sm mb-3">{t.orWhatsApp}</p>
                <motion.a 
                  href="https://wa.me/15513548697" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#25D366]/10 border border-[#25D366]/30 text-[#25D366] font-medium hover:bg-[#25D366]/20 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <MessageCircle size={18} />WhatsApp
                </motion.a>
              </div>
            </motion.form>
            </AnimatedSection>
          )}
        </div>
      </section>
    </div>
  );
};

export default MorristownVisibilityPage;
